const {
  disableTrapFocus,
  fetchJSON,
  focusFirstFocusableElement,
  setLocalStorage,
  getLocalStorage,
  trapFocus,
} = window.BAO.Utils

class LocationModal extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.initialise()
  }

  setupListeners () {
    super.setupListeners()

    if (this.els?.close?.exists) {
      this.els.close.elements.forEach(closeEl => {
        this.listeners.add(closeEl, 'click', this.handleCloseClick.bind(this))
      })
    }

    this.listeners.add(
      document,
      'drawerClosed',
      this.handleDrawerClose.bind(this),
    )

    this.listeners.add(
      document.documentElement,
      'keydown',
      this.handleKeydown.bind(this),
    )
  }

  /**
   * Blocked while close is disabled (country mismatch forces an explicit choice).
   * @returns {void}
   */
  handleKeydown (e) {
    if (e.key !== 'Escape' || !this.isOpen) return

    const closeIsDisabled =
      this.els?.close?.exists &&
      this.els.close.elements.every(closeEl => closeEl.disabled)

    if (closeIsDisabled) return

    this.handleCloseClick()
  }

  async initialise () {
    // User has previously interacted with modal
    if (getLocalStorage(this.storageName)) {
      return
    }

    this.open()

    const customerGeolocationData = await this.getCustomerGeolocationData()
    if (!customerGeolocationData) return

    const customerCountryName =
      customerGeolocationData.detected_values.country.name
    const customerIsoCode =
      customerGeolocationData.detected_values.country.handle

    // Compare user location to Shopify country code and update modal accordingly
    if (customerIsoCode === this.dataset.countryCode) {
      this.setupCorrectLocation()
    } else {
      this.setupIncorrectLocation(customerCountryName, customerIsoCode)
    }
  }

  setupCorrectLocation () {
    if (this.els?.content?.exists) {
      this.els.content.element.classList.add(this.contentLoadedClass)
    }

    if (this.els?.confirm?.exists) {
      this.listeners.add(
        this.els.confirm.element,
        'click',
        this.handleCorrectConfirmClick.bind(this),
      )
    }

    if (this.els?.change?.exists) {
      this.listeners.add(
        this.els.change.element,
        'click',
        this.handleCorrectChangeClick.bind(this),
      )
    }
  }

  setupIncorrectLocation (customerCountryName, customerIsoCode) {
    // Disable close buttons
    if (this.els?.close?.exists) {
      this.els.close.elements?.forEach(closeEl => {
        closeEl.disabled = true
      })
    }

    // Update country name in modal
    if (this.els?.country?.exists) {
      this.els.country.element.textContent = customerCountryName
    }

    // Swap out confirm button href to redirect to correct country version of site
    if (this.els?.confirm?.exists) {
      this.els.confirm.element.href = `${window.location.origin}${window.location.pathname}?country=${customerIsoCode}`

      this.listeners.add(
        this.els.confirm.element,
        'click',
        this.handleIncorrectConfirmClick.bind(this),
      )
    }

    if (this.els?.change?.exists) {
      this.listeners.add(
        this.els.change.element,
        'click',
        this.handleIncorrectChangeClick.bind(this),
      )
    }

    // Unveil modal content now, to ensure the user doesn't see the change of content
    if (this.els?.content?.exists) {
      this.els.content.element.classList.add(this.contentLoadedClass)
    }
  }

  handleCorrectConfirmClick (e) {
    e.preventDefault()

    this.pushEvent('Welcome_pop_up_confirm')

    this.setStorage()

    this.close()
  }

  handleIncorrectConfirmClick (e) {
    const confirmEl = e.target.closest(this.els.confirm.selector)

    if (!confirmEl) return

    e.preventDefault()

    this.setStorage()

    this.pushEvent('Store_switcher_pop_up_confirm')

    window.location.href = confirmEl.getAttribute('href')
  }

  handleCorrectChangeClick () {
    this.pushEvent('Welcome_pop_up_change_country')
  }

  handleIncorrectChangeClick () {
    this.pushEvent('Store_switcher_pop_up_change_country')
  }

  handleCloseClick () {
    this.setStorage()

    this.pushEvent('Welcome_pop_up_dismiss')

    this.close()
  }

  handleDrawerClose (e) {
    if (e.detail?.key === 'shipping-selector' && this.isOpen === true) {
      document.body.classList.add('util-SiteOverflowed')
    }
  }

  open () {
    this.isOpen = true

    this.setAttribute('aria-hidden', 'false')

    trapFocus(this)
    focusFirstFocusableElement(this)

    document.body.classList.add('util-SiteOverflowed')
  }

  close () {
    this.isOpen = false

    this.setAttribute('aria-hidden', 'true')

    disableTrapFocus(this)

    document.body.classList.remove('util-SiteOverflowed')
  }

  setStorage () {
    setLocalStorage(this.storageName, 'true', this.storageDurationMs)
  }

  pushEvent (eventName) {
    window.dataLayer = window.dataLayer || []

    window.dataLayer.push({
      event: eventName,
    })
  }

  async getCustomerGeolocationData () {
    try {
      const res = await fetchJSON(`/browsing_context_suggestions.json`, {
        method: 'GET',
      })

      return res
    } catch (err) {
      console.error('Failed to fetch customer geolocation data', err)
      throw err
    }
  }

  get storageName () {
    return `BAO-location-modal-viewed`
  }

  get storageDurationMs () {
    return 2592000000 // 30 days
  }

  get searchParams () {
    return new URLSearchParams(window.location.search)
  }

  get contentLoadedClass () {
    return `mod-LocationModal_Content-loaded`
  }
}

if (!customElements.get('location-modal')) {
  customElements.define('location-modal', LocationModal)
}
